/**
 * useTaskSectionState
 *
 * Tracks completed / expanded state for homepage task sections.
 *
 * Backed by the homepage_state JSONB endpoint — `taskSections` key.
 */
import { useCallback } from "react";
import {
  getTaskSections,
  useHomepageState,
  useMergeHomepageState,
} from "@src/packages/patients/hooks/useHomepageState";
import { useManualStepCompletion } from "./useManualStepCompletion";

export function useTaskSectionState(
  sectionId: string,
  {
    defaultExpanded = true,
    completionStepId = sectionId,
  }: { defaultExpanded?: boolean; completionStepId?: string } = {},
) {
  const { data: state } = useHomepageState();
  const taskSections = getTaskSections(state);
  const { mutate: mergeHomepage } = useMergeHomepageState();
  const { completed: completedStepIds, setComplete } =
    useManualStepCompletion();

  const completed = completedStepIds.includes(completionStepId);
  const expanded =
    taskSections.expanded[sectionId] !== undefined
      ? !!taskSections.expanded[sectionId]
      : defaultExpanded && !completed;

  const setCompleted = useCallback(
    (nextCompleted: boolean) => {
      setComplete(completionStepId, nextCompleted);
    },
    [completionStepId, setComplete],
  );

  const setExpanded = useCallback(
    (nextExpanded: boolean) => {
      mergeHomepage({
        taskSections: {
          completed: taskSections.completed,
          expanded: {
            ...taskSections.expanded,
            [sectionId]: nextExpanded,
          },
        },
      });
    },
    [sectionId, taskSections, mergeHomepage],
  );

  const toggleCompleted = useCallback(() => {
    setCompleted(!completed);
  }, [completed, setCompleted]);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded, setExpanded]);

  return {
    completed,
    expanded,
    setCompleted,
    setExpanded,
    toggleCompleted,
    toggleExpanded,
  };
}
